import { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import CallServer from './CallServerV2';
import { IS_NATIVE } from '../services/auth/nativeAuth';

// On native <img>, <iframe>, pdf.js and links can't send the Bearer header, so secured
// /get_file urls are swapped for the signed storage url (valid 30 min) returned by the API.
// On web the url is returned untouched: the cookie does the job.
const signedUrls = new Map();

export const resolveFileUrl = async (url) => {
    const fileId = IS_NATIVE && url?.includes('/get_file?')
        && new URL(url, window.location.href).searchParams.get('file_id');
    if (!fileId) return url;
    const cached = signedUrls.get(fileId);
    if (cached && new Date(cached.expires_at) - Date.now() > 60 * 1000) return cached.url;
    const response = await CallServer({
        endpoint: '/get_secured_file_without_redirect',
        params: { file_id: fileId },
        isGet: true,
        retryIfFailed: true,
    });
    // logged out (e.g. login screen logos): public files are served without auth anyway
    if (!response.success) return url;
    signedUrls.set(fileId, response.data);
    return response.data.url;
};

// null on native until the signed url is ready
export const useFileUrl = (url) => {
    const [resolved, setResolved] = useState(IS_NATIVE ? null : url);
    useEffect(() => {
        let active = true;
        resolveFileUrl(url).then(u => active && setResolved(u));
        return () => { active = false };
    }, [url]);
    return resolved;
};

// target=_blank links (files, certificates, notifications, legal pdfs) open in the in-app browser;
// mailto:, tel:, wa.me... are left to the OS
export const openLinksInNativeBrowser = () => {
    if (!IS_NATIVE) return;
    document.addEventListener('click', async (e) => {
        const link = e.target.closest?.('a[target="_blank"]');
        if (!link?.href) return;
        const url = new URL(link.href);
        // compared by protocol+host: URL.origin is "null" for capacitor:// on iOS
        const isBundled = url.protocol === window.location.protocol && url.host === window.location.host;
        if (!isBundled && !/^https?:$/.test(url.protocol)) return;
        e.preventDefault();
        // bundled assets (e.g. /static/legals/*.pdf) live on the web host as well
        const href = isBundled ? `${process.env.REACT_APP_API_URL}${url.pathname}` : link.href;
        await Browser.open({ url: await resolveFileUrl(href) });
    }, true);
};
